import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminBalanceService } from '@/shared/api'
import type { InvoiceCreateRequest, InvoiceListFilters } from '@/shared/api'

const KEYS = {
  balance: (entityId: number) => ['balance', entityId],
  invoices: (filters?: InvoiceListFilters) => ['balance-invoices', filters],
  operations: (entityId: number, page: number, limit: number) =>
    ['balance-operations', entityId, page, limit],
  holds: (entityId: number) => ['balance-holds', entityId],
}

interface QueryOptions {
  enabled?: boolean
}

interface ApiMutationError {
  alreadyToasted?: boolean
  detail?: string
}

/** Мутации баланса меняют сразу все связанные выборки. */
const invalidateBalance = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['balance'] })
  queryClient.invalidateQueries({ queryKey: ['balance-invoices'] })
  queryClient.invalidateQueries({ queryKey: ['balance-operations'] })
  queryClient.invalidateQueries({ queryKey: ['balance-holds'] })
}

const toastError = (fallback: string) => (error: ApiMutationError) => {
  if (!error?.alreadyToasted) toast.error(error?.detail || fallback)
}

/** Снимок демо-баланса компании */
export function useBalance(entityId: number, options?: QueryOptions) {
  return useQuery({
    queryKey: KEYS.balance(entityId),
    queryFn: () => AdminBalanceService.balance(entityId),
    enabled: options?.enabled !== false && entityId > 0,
    staleTime: 30000,
  })
}

/** Счета всех юрлиц: статус, юрлицо, период, поиск по номеру */
export function useInvoices(
  filters?: InvoiceListFilters,
  options?: QueryOptions
) {
  return useQuery({
    queryKey: KEYS.invoices(filters),
    queryFn: () => AdminBalanceService.invoices(filters),
    enabled: options?.enabled !== false,
    staleTime: 30000,
  })
}

/** Журнал операций компании страницей */
export function useOperations(
  entityId: number,
  page = 1,
  limit = 10,
  options?: QueryOptions
) {
  return useQuery({
    queryKey: KEYS.operations(entityId, page, limit),
    queryFn: () => AdminBalanceService.operations(entityId, { page, limit }),
    enabled: options?.enabled !== false && entityId > 0,
    staleTime: 30000,
  })
}

/** Активные резервы компании */
export function useHolds(entityId: number, options?: QueryOptions) {
  return useQuery({
    queryKey: KEYS.holds(entityId),
    queryFn: () => AdminBalanceService.holds(entityId),
    enabled: options?.enabled !== false && entityId > 0,
    staleTime: 30000,
  })
}

/** Выставить счёт из админки */
export function useCreateInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entityId, body }: { entityId: number; body: InvoiceCreateRequest }) =>
      AdminBalanceService.createInvoice(entityId, body),
    onSuccess: () => {
      toast.success('Счёт создан')
      invalidateBalance(queryClient)
    },
    onError: toastError('Ошибка создания счёта'),
  })
}

/** Подтвердить демонстрационную оплату счёта */
export function useConfirmInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: number) => AdminBalanceService.confirmInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Оплата подтверждена, баланс пополнен')
      invalidateBalance(queryClient)
    },
    onError: toastError('Ошибка подтверждения оплаты'),
  })
}

/** Аннулировать неоплаченный счёт */
export function useCancelInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: number; reason: string }) =>
      AdminBalanceService.cancelInvoice(invoiceId, reason),
    onSuccess: () => {
      toast.success('Счёт аннулирован')
      invalidateBalance(queryClient)
    },
    onError: toastError('Ошибка аннулирования счёта'),
  })
}

/** Освободить зависший резерв вручную */
export function useReleaseHold() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entityId, entryId }: { entityId: number; entryId: number }) =>
      AdminBalanceService.releaseHold(entityId, entryId),
    onSuccess: () => {
      toast.success('Резерв освобождён')
      invalidateBalance(queryClient)
    },
    onError: toastError('Ошибка освобождения резерва'),
  })
}

/** Включить или выключить демо-баланс компании */
export function useUpdateBalanceSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entityId, enabled }: { entityId: number; enabled: boolean }) =>
      AdminBalanceService.updateSettings(entityId, enabled),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.enabled ? 'Демо-баланс включён' : 'Демо-баланс выключен'
      )
      invalidateBalance(queryClient)
    },
    onError: toastError('Ошибка смены демо-режима'),
  })
}

/** Скачать PDF счёта с именем файла из Content-Disposition */
export async function downloadInvoicePdf(invoiceId: number): Promise<void> {
  const { blob, filename } = await AdminBalanceService.invoicePdf(invoiceId)
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename || 'invoice.pdf')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
